import * as React from 'react'
import styles from './relation-series.module.scss'
import Divider from 'Components/divider'
import CommonLink, { ECommonLinkType } from 'Components/links/common-link'
import classNames from 'classnames'
export interface IContentRelationSeriesProps {
  uriKey?: string
  seriesId?: number | string
  seriesName?: string
  brandId?: number
  price?: string
  cutPrice?: string
  hasPrice?: boolean
  hasCutPrice?: boolean
  hasImage: boolean
  hasTest?: boolean
  carIds?: number[]
  score?: number
  communityCount?: number | string
  hasUsedCar?: boolean
  usedCarPrice?: number
  usedCarCount?: number
  zt?: string
  linkSource?: string
  onClick?(
    e: React.MouseEvent,
    type: string,
    params?: Record<string, any>
  ): void
}
/*
 该组件被ReactDOMServer.renderToString调用
 通过父级组件调用此方法绑定事件
 跳转二手车在客户端修改
*/
export const addRelationSeriesListeners = (
  parentRef: React.RefObject<HTMLDivElement>,
  handlers?: (e: Event, type: string, params?: Record<string, any>) => void
) => {
  const eventHandler = (event: Event) => {
    const el = event.currentTarget as Element
    if (el.parentElement?.classList.contains('disabled')) return
    const type = el.getAttribute('data-click') || ''
    const params = JSON.parse(el.getAttribute('data-params') || `{}`) as Record<
      string,
      any
    >
    handlers?.(event, type, params)
  }
  React.useEffect(() => {
    if (!parentRef.current) return
    const elements = parentRef.current.querySelectorAll('[data-click]')
    elements.forEach((el) => {
      el.addEventListener('click', eventHandler)
    })
    return () => {
      elements.forEach((el) => el.removeEventListener('click', eventHandler))
    }
  }, [])
}
export default function ContentRelationSeries({
  uriKey,
  seriesId,
  seriesName,
  brandId,
  price,
  cutPrice,
  hasPrice,
  hasCutPrice,
  hasImage,
  hasTest,
  carIds,
  score,
  communityCount,
  hasUsedCar,
  usedCarPrice,
  usedCarCount,
  zt,
  linkSource,
  onClick,
}: IContentRelationSeriesProps) {
  const None = () => <span className={styles.none}>暂无</span>
  const commonParams = { car_series_id: seriesId, car_series_name: seriesName }
  // 客户端替换时，通过data-image获取根节点，通过不传uriKey获取卡片内容部分
  const Wrapper = ({ children }: { children: React.ReactNode }) =>
    uriKey ? (
      <div data-image={uriKey}>{children}</div>
    ) : (
      <React.Fragment>{children}</React.Fragment>
    )
  const handleClick = (
    event: React.MouseEvent,
    type: string,
    extraParams?: Record<string, any>
  ) => {
    const el = event.currentTarget as Element
    if (el.parentElement?.classList.contains('disabled')) return
    onClick?.(event, type, { ...commonParams, ...extraParams })
  }
  return (
    <Wrapper>
      <div className={styles.root}>
        <div className={styles.info}>
          <CommonLink
            type={ECommonLinkType.SERIES}
            id={seriesId}
            className="tw-flex-1"
          >
            <div
              className={styles['info-series']}
              data-click="series"
              data-params={JSON.stringify(commonParams)}
              onClick={(e) => handleClick(e, 'series')}
            >
              <strong className={styles['info-series-name']}>
                {seriesName}
              </strong>
              <div className={styles['info-price']}>
                <span className={styles['info-price-item']}>
                  <span className={styles['info-price-label']}>最低售价：</span>
                  <span className={styles['info-price-dealer']}>{price}</span>
                </span>
                {hasCutPrice && (
                  <span className={styles['info-price-item']}>
                    <span className={styles['info-price-label']}>
                      最高降价：
                    </span>
                    <span className={styles['info-price-cut']}>
                      <i
                        className={classNames(
                          'DCD_Icon icon_reduce',
                          styles['info-price-cut-icon']
                        )}
                      />
                      {cutPrice}
                    </span>
                  </span>
                )}
              </div>
            </div>
          </CommonLink>
          <div className={styles['info-link-wrapper']}>
            <CommonLink
              className={styles['info-link-anchor']}
              id={seriesId}
              type={ECommonLinkType.IMAGE_LIST}
              disabled={!hasImage}
              title={hasImage ? '' : '暂无图片'}
            >
              <span
                className={classNames(
                  styles['info-link'],
                  styles['info-link-normal']
                )}
                data-click="image"
                data-params={JSON.stringify({
                  ...commonParams,
                  button_name: '图片',
                })}
                onClick={(e) =>
                  handleClick(e, 'image', { button_name: '图片' })
                }
              >
                图片
              </span>
            </CommonLink>
            <CommonLink
              id={seriesId}
              className={styles['info-link-anchor']}
              type={ECommonLinkType.CONFIG}
              title={carIds?.length ? '' : '暂无参数'}
              disabled={!carIds?.length}
            >
              <span
                className={classNames(
                  styles['info-link'],
                  styles['info-link-normal']
                )}
                data-click="config"
                data-params={JSON.stringify({
                  ...commonParams,
                  button_name: '参数配置',
                })}
                onClick={(e) =>
                  handleClick(e, 'config', { button_name: '参数配置' })
                }
              >
                参数配置
              </span>
            </CommonLink>
            <CommonLink
              className={styles['info-link-anchor']}
              id={seriesId}
              type={ECommonLinkType.DEALER}
              disabled={!hasPrice}
              title={hasPrice ? '' : '暂无报价'}
              query={{ zt, link_source: linkSource }}
              rel="opener nofollow"
            >
              <span
                className={classNames(
                  styles['info-link'],
                  styles['info-link-highlight']
                )}
                data-click="dealer"
                data-params={JSON.stringify({
                  ...commonParams,
                  button_name: '询底价',
                })}
                onClick={(e) =>
                  handleClick(e, 'dealer', { button_name: '询底价' })
                }
              >
                {hasPrice ? '询底价' : '暂无报价'}
              </span>
            </CommonLink>
          </div>
        </div>
        <Divider
          width="100%"
          height="1px"
          color="transparent"
          style={{ display: 'block' }}
        />
        <div className={styles.footer}>
          <CommonLink
            className={styles['footer-link-anchor']}
            id={seriesId}
            type={ECommonLinkType.SCORE}
            disabled={!score}
          >
            <span
              className={styles['footer-link']}
              data-click="score"
              data-params={JSON.stringify({
                ...commonParams,
                button_name: '懂车分',
              })}
              onClick={(e) =>
                handleClick(e, 'score', { button_name: '懂车分' })
              }
            >
              <span>懂车分</span>
              <span className={styles['footer-link-subtext']}>
                <span>{score ? (score / 100).toFixed(2) : <None />}</span>
                <i
                  className={classNames(
                    'DCD_Icon icon_into_12',
                    styles['footer-link-icon']
                  )}
                />
              </span>
            </span>
          </CommonLink>
          <CommonLink
            className={styles['footer-link-anchor']}
            id={seriesId}
            type={ECommonLinkType.TEST}
            query={{ head_right_tab: 'eval' }}
            disabled={!hasTest}
          >
            <span
              className={styles['footer-link']}
              data-click="eval"
              data-params={JSON.stringify({
                ...commonParams,
                button_name: '懂车实测',
              })}
              onClick={(e) =>
                handleClick(e, 'eval', { button_name: '懂车实测' })
              }
            >
              <span>懂车实测</span>
              <span className={styles['footer-link-subtext']}>
                {hasTest ? '空间·性能等' : <None />}
                <i
                  className={classNames(
                    'DCD_Icon icon_into_12',
                    styles['footer-link-icon']
                  )}
                />
              </span>
            </span>
          </CommonLink>
          <CommonLink
            className={styles['footer-link-anchor']}
            id={seriesId}
            type={ECommonLinkType.COMMUNITY}
          >
            <span
              className={styles['footer-link']}
              data-click="community"
              data-params={JSON.stringify({
                ...commonParams,
                button_name: '车友圈',
              })}
              onClick={(e) =>
                handleClick(e, 'community', { button_name: '车友圈' })
              }
            >
              <span>车友圈</span>
              <span className={styles['footer-link-subtext']}>
                {communityCount}车友热议
                <i
                  className={classNames(
                    'DCD_Icon icon_into_12',
                    styles['footer-link-icon']
                  )}
                />
              </span>
            </span>
          </CommonLink>
          <CommonLink
            className={styles['footer-link-anchor']}
            id={seriesId}
            type={ECommonLinkType.USED_CAR}
            href={`/usedcar/x-x-x-x-x-x-x-x-x-x-x-x-x-x-x-x-x-x-x-${brandId}-${seriesId}-x-x-x-x-x-x`}
            disabled={!hasUsedCar}
          >
            <span
              className={styles['footer-link']}
              data-click="usedCar"
              data-params={JSON.stringify({
                ...commonParams,
                button_name: '二手车',
              })}
              onClick={(e) =>
                handleClick(e, 'usedCar', { button_name: '二手车' })
              }
            >
              <span>二手车</span>
              <span className={styles['footer-link-subtext']}>
                {hasUsedCar ? (
                  <span>
                    {usedCarPrice &&
                      `${(usedCarPrice / 1000000).toFixed(2)}万起 | `}
                    {usedCarCount}辆
                  </span>
                ) : (
                  <None />
                )}
                <i
                  className={classNames(
                    'DCD_Icon icon_into_12',
                    styles['footer-link-icon']
                  )}
                />
              </span>
            </span>
          </CommonLink>
        </div>
      </div>
    </Wrapper>
  )
}
